import { Button } from '@design/components/button/Button';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { InsightsLivePriceChange } from '@terminal/components/insights-live-price-change/InsightsLivePriceChange';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './InsightDrawerAssetHeaderContent.module.scss';
import { InsightDrawerHeader } from './InsightDrawerHeader';
import type { InsightDrawerAssetHeaderContentProps } from './insightDrawerHeaderTypes';

export const InsightDrawerAssetHeaderContent = ({
  entity,
  source,
}: InsightDrawerAssetHeaderContentProps) => {
  const { t } = useTranslation(['common']);

  const { navigateToAssetOverview } = useNavigateToAssetOverview();
  const { trackWatchlistCreate } = usePosthogEvents();
  const isInAnyWatchlist = useWatchlist(
    state => !!state.listIdsByTag.get(entity.tag)?.length
  );

  const handleOpenOverview = () => {
    navigateToAssetOverview(entity.tag, source);
  };

  const handleWatchlistCreated = (watchlistId: string) => {
    trackWatchlistCreate(source, watchlistId);
  };

  const entityName = entity.name_short || entity.name_full;
  const watchlistLabel = isInAnyWatchlist
    ? t('common:components.watchlistBtn.watching')
    : t('common:components.watchlistBtn.addToWatchlistLabel');

  return (
    <InsightDrawerHeader
      action={
        <Tooltip
          inPortal
          isTouchDevice={false}
          label={watchlistLabel}
          trigger={[TooltipTrigger.Hover, TooltipTrigger.Focus]}
        >
          <div className={styles.watchlistTooltipTrigger}>
            <WatchlistTrigger
              onWatchlistCreated={handleWatchlistCreated}
              tag={entity.tag}
              dropdownProps={{
                strategy: 'fixed',
                placement: 'top-start',
                inPortal: false,
              }}
              renderTrigger={isInWatchlist => (
                <Button
                  aria-label={watchlistLabel}
                  className={clsx(styles.watchlistButton, {
                    [styles.watchlistButtonActive]: isInWatchlist,
                  })}
                  data-testid="watchlist-button"
                  data-insight-drawer-watchlist-button
                  iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                  size="xsmall"
                  variant="tertiary"
                />
              )}
            />
          </div>
        </Tooltip>
      }
      logo={<AssetLogoContainer entity={entity} size={44} />}
      name={entityName}
      onOpenOverview={handleOpenOverview}
      performance={<InsightsLivePriceChange entity={entity} waitForLiveTick />}
      variant="asset"
    />
  );
};
